import { DateTime } from "luxon";
import { useCallback, useEffect, useRef } from "react";

import { UserStatus } from "@/types/user-statuses.types";

type UseInactivityDetectorOptions = {
    timeout?: number;
    onInactive: () => void;
    onActive: () => void;
    isEnabled?: boolean;
    currentStatus: UserStatus | null;
};

const EVENTS_TO_TRACK = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"] as const;

const DEFAULT_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useInactivityDetector = ({
    timeout = DEFAULT_TIMEOUT,
    onInactive,
    onActive,
    isEnabled = true,
    currentStatus,
}: UseInactivityDetectorOptions) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(DateTime.now().toMillis());
    const isInactiveRef = useRef<boolean>(false);
    const previousStatusRef = useRef<UserStatus | null>(null);
    const onInactiveRef = useRef(onInactive);
    const onActiveRef = useRef(onActive);

    // Update callback refs when they change
    useEffect(() => {
        onInactiveRef.current = onInactive;
        onActiveRef.current = onActive;
    }, [onInactive, onActive]);

    // Reset inactive state when status changes from INACTIVE
    useEffect(() => {
        if (currentStatus !== UserStatus.INACTIVE && isInactiveRef.current) {
            isInactiveRef.current = false;
            previousStatusRef.current = null;
        }
    }, [currentStatus]);

    const resetTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (!isEnabled || !currentStatus) return;

        // Only set timeout for active statuses (not OFFLINE, INACTIVE, or IN_CALL)
        if (
            currentStatus !== UserStatus.OFFLINE &&
            currentStatus !== UserStatus.INACTIVE &&
            currentStatus !== UserStatus.IN_CALL
        ) {
            timeoutRef.current = setTimeout(() => {
                if (!isInactiveRef.current) {
                    isInactiveRef.current = true;
                    previousStatusRef.current = currentStatus;
                    onInactiveRef.current();
                }
            }, timeout);
        }
    }, [isEnabled, currentStatus, timeout]);

    const handleActivity = useCallback(() => {
        lastActivityRef.current = DateTime.now().toMillis();

        // If user was inactive, mark as active and restore previous status
        if (isInactiveRef.current) {
            isInactiveRef.current = false;
            onActiveRef.current();
        }

        resetTimeout();
    }, [resetTimeout]);

    useEffect(() => {
        if (!isEnabled) {
            // Clean up when disabled
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            isInactiveRef.current = false;
            return;
        }

        // Add event listeners for activity detection
        EVENTS_TO_TRACK.forEach((event) => {
            document.addEventListener(event, handleActivity, true);
        });

        // Initialize timeout
        resetTimeout();

        return () => {
            // Cleanup event listeners
            EVENTS_TO_TRACK.forEach((event) => {
                document.removeEventListener(event, handleActivity, true);
            });

            // Clear timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isEnabled, handleActivity, resetTimeout]);

    return {
        isInactive: isInactiveRef.current,
        previousStatus: previousStatusRef.current,
        lastActivity: lastActivityRef.current,
    };
};
